import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@stores/toastStore";

import {
  TierUpgradeBlockedError,
  requirementsApi,
} from "../services/api";
import { requirementKeys } from "../services/queryKeys";
import type {
  CreateRequirementBody,
  PatchRequirementBody,
  RequirementsSpec,
  TierUpgradeReport,
  UpdateSpecBody,
} from "../types";

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * Read the project's full requirements spec. Backend lazy-creates the
 * spec row on first read, so this also bootstraps a brand-new project
 * without a separate "create spec" call.
 */
export function useRequirementsSpec(projectId: string | null | undefined) {
  return useQuery({
    queryKey: requirementKeys.spec(projectId ?? ""),
    queryFn: () => {
      // `enabled: !!projectId` gates this; the runtime check narrows the
      // type without an `as` escape.
      if (!projectId) throw new Error("projectId is required");
      return requirementsApi.getSpec(projectId);
    },
    enabled: !!projectId,
  });
}

/* ── Requirement mutations ────────────────────────────────────────────── */

export function useCreateRequirement(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRequirementBody) =>
      requirementsApi.create(projectId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: requirementKeys.spec(projectId) });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not create requirement"));
    },
  });
}

export function usePatchRequirement(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      body,
    }: {
      publicId: string;
      body: PatchRequirementBody;
    }) => requirementsApi.patch(projectId, publicId, body),
    // Optimistic update: stamp the new fields on the cached spec
    // immediately so the editor doesn't flash old values on save. If
    // the request fails, the rollback restores the previous snapshot.
    onMutate: async ({ publicId, body }) => {
      const key = requirementKeys.spec(projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<RequirementsSpec>(key);
      if (previous) {
        qc.setQueryData<RequirementsSpec>(key, {
          ...previous,
          requirements: previous.requirements.map((r) =>
            r.id === publicId
              ? {
                  ...r,
                  ...body,
                  // updated_at is computed server-side; bump locally so
                  // the row's "updated X ago" reads sensibly until the
                  // server response lands.
                  updated_at: new Date().toISOString(),
                }
              : r,
          ),
        });
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(requirementKeys.spec(projectId), ctx.previous);
      }
      toast.error(errorMessage(err, "Could not save requirement"));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: requirementKeys.spec(projectId) });
    },
  });
}

/* ── Suggestion review (accept / reject) ──────────────────────────────── */
//
// These are the user-facing controls on a `status='suggested'` row in
// the requirements list. Both pass `sessionId` through so the backend
// stamps a delta onto the active chat session — the requirements
// agent's next turn reads it from the system-reminder.

export function useAcceptSuggestion(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      sessionId,
    }: {
      publicId: string;
      sessionId: string | null;
    }) => requirementsApi.acceptSuggestion(projectId, publicId, sessionId),
    onMutate: async ({ publicId }) => {
      const key = requirementKeys.spec(projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<RequirementsSpec>(key);
      if (previous) {
        qc.setQueryData<RequirementsSpec>(key, {
          ...previous,
          requirements: previous.requirements.map((r) =>
            r.id === publicId ? { ...r, status: "draft" } : r,
          ),
        });
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(requirementKeys.spec(projectId), ctx.previous);
      }
      toast.error(errorMessage(err, "Could not accept suggestion"));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: requirementKeys.spec(projectId) });
    },
  });
}

export function useRejectSuggestion(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      publicId,
      sessionId,
    }: {
      publicId: string;
      sessionId: string | null;
    }) => requirementsApi.rejectSuggestion(projectId, publicId, sessionId),
    onMutate: async ({ publicId }) => {
      const key = requirementKeys.spec(projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<RequirementsSpec>(key);
      if (previous) {
        qc.setQueryData<RequirementsSpec>(key, {
          ...previous,
          requirements: previous.requirements.filter((r) => r.id !== publicId),
        });
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(requirementKeys.spec(projectId), ctx.previous);
      }
      toast.error(errorMessage(err, "Could not reject suggestion"));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: requirementKeys.spec(projectId) });
    },
  });
}

export function useDeleteRequirement(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) =>
      requirementsApi.remove(projectId, publicId),
    onMutate: async (publicId) => {
      const key = requirementKeys.spec(projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<RequirementsSpec>(key);
      if (previous) {
        qc.setQueryData<RequirementsSpec>(key, {
          ...previous,
          requirements: previous.requirements.filter((r) => r.id !== publicId),
        });
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(requirementKeys.spec(projectId), ctx.previous);
      }
      toast.error(errorMessage(err, "Could not delete requirement"));
    },
    onSuccess: (_data, publicId) => {
      void qc.invalidateQueries({ queryKey: requirementKeys.spec(projectId) });
      toast.success(`Deleted ${publicId}`);
    },
  });
}

/* ── Tier mutations ───────────────────────────────────────────────────── */

export function usePreviewTierUpgrade(projectId: string) {
  return useMutation({
    mutationFn: (target: UpdateSpecBody) =>
      requirementsApi.previewTierUpgrade(projectId, target),
    onError: (err) => {
      toast.error(errorMessage(err, "Could not preview tier upgrade"));
    },
  });
}

export interface UpdateSpecResult {
  ok: boolean;
  report: TierUpgradeReport | null;
}

export function useUpdateSpec(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateSpecBody): Promise<UpdateSpecResult> => {
      try {
        const report = await requirementsApi.updateSpec(projectId, body);
        return { ok: true, report };
      } catch (err) {
        if (err instanceof TierUpgradeBlockedError) {
          // Blocking gaps prevented the upgrade. Surface the report so
          // the UI can render the gap list; this isn't an error from
          // the user's perspective, just a "you have N gaps to fix".
          return { ok: false, report: err.report };
        }
        throw err;
      }
    },
    onSuccess: (result) => {
      if (result.ok) {
        void qc.invalidateQueries({
          queryKey: requirementKeys.spec(projectId),
        });
        toast.success(`Tier set to ${result.report?.to_tier ?? "—"}.`);
      }
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Could not update tier"));
    },
  });
}
